import { useCallback, type RefObject } from 'react';
import { createRoot } from 'react-dom/client';
import { toast } from '@/hooks/use-toast';
import { renderNodeAsPng, deliverPng } from '@/integrations/export/png';
import { renderNodeAsSvg, deliverSvg } from '@/integrations/export/svg';
import { OverviewExportCanvas } from '@/components/workflow/OverviewExportCanvas';
import type { WorkflowBlock, Connector, BlockGroup as Group } from '@/types/workflow';
import { useUserActivity } from '@/hooks/useUserActivity';

export interface UseExportHandlersDeps {
  canvasInnerRef: RefObject<HTMLDivElement>;
  workflowName: string;
  blocks: WorkflowBlock[];
  connectors: Connector[];
  groups: Group[];
  setExportPngDialogOpen: (v: boolean) => void;
  t: (key: string) => string;
}

export function useExportHandlers(deps: UseExportHandlersDeps) {
  const {
    canvasInnerRef, workflowName, blocks, connectors, groups,
    setExportPngDialogOpen, t,
  } = deps;

  // #96 Wave C.2 — opt-in event-tracking. Edge-fn:en no-op:ar om user
  // inte är opted-in. Fire-and-forget vid framgångsrik export.
  const userActivity = useUserActivity();

  /** Open the PNG export format-selector dialog. */
  const handleExportPNG = useCallback(() => {
    setExportPngDialogOpen(true);
  }, [setExportPngDialogOpen]);

  /** Export the current canvas view (what the user sees) as PNG or SVG. */
  const handleExportView = useCallback(async (format: 'png' | 'svg' = 'png') => {
    const node = canvasInnerRef.current
      ?? (document.querySelector('.canvas-inner') as HTMLElement | null);
    if (!node) {
      toast({ title: t('error'), description: 'Export failed', variant: 'destructive' });
      return;
    }
    const baseName = workflowName || 'flow-weaver';
    const opts = { width: node.scrollWidth, height: node.scrollHeight };
    // Persistent toast while the browser rasterizes — large canvases
    // can spend 3–8 s in html-to-image and the user has no other signal
    // that the export is in flight. Dismissed on success/error.
    const progressToast = toast({
      title: `${t('export')} ${format.toUpperCase()}…`,
      description: `${t('pleaseWait') ?? 'Processing…'}`,
      duration: 60_000,
    });
    try {
      if (format === 'svg') {
        const dataUrl = await renderNodeAsSvg(node, opts);
        deliverSvg(dataUrl, `${baseName}.svg`);
      } else {
        const dataUrl = await renderNodeAsPng(node, opts);
        deliverPng(dataUrl, `${baseName}.png`);
      }
      void userActivity.log('export', { format, kind: 'view' });
      progressToast.update({
        id: progressToast.id,
        title: t('exportDone') ?? 'Export done',
        description: `${baseName}.${format}`,
        duration: 3000,
      });
    } catch {
      progressToast.dismiss();
      toast({ title: t('error'), description: `${format.toUpperCase()} export failed`, variant: 'destructive' });
    }
  }, [canvasInnerRef, workflowName, t, userActivity]);

  /** Export the whole workflow (all sheets + groups) via OverviewExportCanvas. */
  const handleExportAll = useCallback(async (
    format: 'png' | 'svg' = 'png',
    viewMode?: 'flat' | 'detailed' | 'tree' | 'compact',
  ) => {
    const container = document.createElement('div');
    container.style.cssText = 'position:fixed;left:-9999px;top:0;z-index:-1;';
    document.body.appendChild(container);

    const root = createRoot(container);
    root.render(
      <OverviewExportCanvas
        blocks={blocks}
        connectors={connectors}
        groups={groups}
        viewMode={viewMode}
      />,
    );

    await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));

    const baseName = workflowName || 'flow-weaver';
    const progressToast = toast({
      title: `${t('export')} ${format.toUpperCase()}…`,
      description: `${t('pleaseWait') ?? 'Rendering overview…'}`,
      duration: 60_000,
    });
    try {
      const target = container.firstElementChild as HTMLElement;
      if (!target) throw new Error('No element');
      if (format === 'svg') {
        const dataUrl = await renderNodeAsSvg(target);
        deliverSvg(dataUrl, `${baseName}-overview.svg`);
      } else {
        const dataUrl = await renderNodeAsPng(target);
        deliverPng(dataUrl, `${baseName}-overview.png`);
      }
      progressToast.update({
        id: progressToast.id,
        title: t('exportDone') ?? 'Export done',
        description: `${baseName}-overview.${format}`,
        duration: 3000,
      });
    } catch {
      progressToast.dismiss();
      toast({ title: t('error'), description: `${format.toUpperCase()} export failed`, variant: 'destructive' });
    } finally {
      root.unmount();
      document.body.removeChild(container);
    }
  }, [blocks, connectors, groups, workflowName, t]);

  return { handleExportPNG, handleExportView, handleExportAll };
}
